import { useSortable } from '@dnd-kit/sortable'
import React, { memo, type ReactNode, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'

import {
  TtButton,
  TtDropdown,
  TtIcon,
  TtIconButton,
  TtMenu,
  TtMenuItem,
  TtTrayItem,
} from '@23/react'

import {
  removeCard,
  useTray,
  useTrayCardIndex,
} from '../../../firebase/hooks/useTray'

import GutterMenu from './GutterMenu/GutterMenu'
import { setTrayCardEditing } from './useTrayEditing'
import { TrayCard } from './TrayCard'

interface TrayItemProps {
  id: string
}

interface TrayItemChildrenConfig {
  isMedia: boolean
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
}

type PropsWithTrayItemChildren<T> = T & {
  children?: (config?: Partial<TrayItemChildrenConfig>) => ReactNode
}

const TrayItem = ({ id }: TrayItemProps) => {
  const index = useTrayCardIndex(id)
  const { cardOrder } = useTray()

  // sortable
  const {
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
    attributes,
    listeners,
  } = useSortable({ id })

  const style = isSorting
    ? {
        transform: `translateY(${transform?.y ?? 0}px)`,
        transition,
        position: 'relative' as const,
        zIndex: isDragging ? 1 : undefined,
        opacity: isDragging ? 0.8 : 1,
      }
    : {}

  const handleEdit = useCallback(() => setTrayCardEditing(id), [id])
  const handleDelete = useCallback(() => removeCard(id), [id])
  const isLastCard = index === cardOrder.length - 1

  return (
    <TtTrayItem
      gutterMode={isSorting ? 'disabled' : isLastCard ? 'persisted' : 'normal'}
      data-id={id}
      gutterDelay={1000}
      ref={setNodeRef}
      style={style}
    >
      <TrayCard id={id}>
        {({
          isMedia,
          canEdit,
          onEdit,
          onDelete,
        }: Partial<TrayItemChildrenConfig> = {}) => (
          <>
            <div slot="toolbar" className="icon-enclosure">
              <TtIcon
                library="one"
                name="drag"
                data-drag-handle="true"
                {...listeners}
                {...attributes}
              />
            </div>
            <TtDropdown slot="toolbar" placement="bottom-start" hoist>
              {isMedia ? (
                <div slot="trigger" className="icon-enclosure">
                  <TtIconButton
                    library="one"
                    name="more"
                    color="secondary"
                    label={intl.formatMessage({
                      defaultMessage: 'more options',
                    })}
                  />
                </div>
              ) : (
                <TtButton
                  slot="trigger"
                  size="x-small"
                  variant="ghost"
                  color="secondary"
                  circle
                  label={intl.formatMessage({ defaultMessage: 'more options' })}
                >
                  <TtIcon library="one" name="more" />
                </TtButton>
              )}
              <TtMenu>
                {canEdit && (
                  <TtMenuItem
                    value="edit"
                    onTt-click={() => {
                      handleEdit()
                      onEdit?.()
                    }}
                  >
                    <FormattedMessage defaultMessage="Edit" />
                  </TtMenuItem>
                )}
                <TtMenuItem
                  value="delete"
                  onTt-click={() => {
                    handleDelete()
                    onDelete?.()
                  }}
                >
                  <FormattedMessage defaultMessage="Delete" />
                </TtMenuItem>
              </TtMenu>
            </TtDropdown>
          </>
        )}
      </TrayCard>
      <GutterMenu cardId={id} />
    </TtTrayItem>
  )
}

const MemoizedTrayItem = memo(TrayItem)

export { MemoizedTrayItem as TrayItem, type PropsWithTrayItemChildren }
